import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';

import { AgreementStatus, useGetAgreementsByIdQuery } from '@client/api';
import dayjs from 'dayjs';

const STATUS_ORDER: AgreementStatus[] = ['Draft', 'Ready', 'Effective', 'Terminated'];

type StepKey = 'draft' | 'ready' | 'effective' | 'terminated';

const STATUS_TO_KEY: Record<AgreementStatus, StepKey> = {
  Draft: 'draft',
  Ready: 'ready',
  Effective: 'effective',
  Terminated: 'terminated',
};

export function AgreementProgressBar() {
  const { t } = useTranslation('agreements');

  const { id } = useParams<{ id: string }>();

  const { data: agreement } = useGetAgreementsByIdQuery(
    { id: id ?? '' },
    { skip: id == null },
  );

  const currentStatus = agreement?.status;
  const currentIndex = currentStatus ? STATUS_ORDER.indexOf(currentStatus) : -1;
  const isEffective = currentStatus === 'Effective';
  const isTerminated = currentStatus === 'Terminated';
  const effectiveDate = agreement?.effectiveAt ? dayjs(agreement.effectiveAt).format('DD.MM.YYYY') : null;
  const terminatedDate = agreement?.terminatedAt ? dayjs(agreement.terminatedAt).format('DD.MM.YYYY') : null;

  return (
    <>
      <div className="px-4 mb-10">
        <div className="bg-card border border-border rounded-xl px-8 py-5">
          {/* Step indicators */}
          <div className="flex items-start w-full">
            {STATUS_ORDER.map((status, index) => {
              const key = STATUS_TO_KEY[status];
              const stepNumber = index + 1;
              const isCompleted = index < currentIndex;
              const isCurrent = index === currentIndex;
              const isLast = index === STATUS_ORDER.length - 1;

              return (
                <React.Fragment key={status}>
                  {/* Step: circle centered in its flex-1 slot */}
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={classNames(
                        'w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all',
                        isCompleted && 'bg-brand-primary border-brand-primary',
                        isCurrent && (isEffective ? 'border-green-500 bg-transparent' : 'border-brand-primary bg-transparent'),
                        !isCompleted && !isCurrent && 'border-white/20 bg-transparent',
                      )}
                    >
                      {isCompleted ? (
                        <FaCheck className="text-black text-sm" />
                      ) : (
                        <span
                          className={classNames(
                            'text-sm font-semibold',
                            isCurrent ? (isEffective ? 'text-white' : 'text-brand-primary') : 'text-white/40',
                          )}
                        >
                          {stepNumber}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 text-center">
                      <div
                        className={classNames(
                          'text-sm font-semibold',
                          isCurrent ? 'text-white' : isCompleted ? 'text-white' : 'text-white/40',
                        )}
                      >
                        {t(`wizard.steps.${key}`)}
                      </div>
                      <div
                        className={classNames(
                          'text-xs mt-0.5',
                          isCurrent || isCompleted ? 'text-white/60' : 'text-white/30',
                        )}
                      >
                        {key === 'effective' && (isEffective || isTerminated) && effectiveDate
                          ? t('wizard.subtitles.effectiveWithDate', { date: effectiveDate })
                          : key === 'terminated' && isTerminated && terminatedDate
                            ? t('wizard.subtitles.terminatedWithDate', { date: terminatedDate })
                            : t(`wizard.subtitles.${key}`)}
                      </div>
                    </div>
                  </div>

                  {/* Connector line between steps */}
                  {!isLast && (
                    <div className="flex-1 flex items-center mt-5">
                      <div
                        className={classNames(
                          'h-px w-full transition-all',
                          index < currentIndex ? 'bg-brand-primary' : 'bg-white/20',
                        )}
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

    </>
  );
}
